import { Tabs, Button } from '@app/components';
import { ContactUsEmail, SettingsTabs, SettingTabs } from '@app/utils/consts';
import { useSettingsPage } from './Settings.hooks';
import { useEffect, useMemo, useState, createContext, useContext } from 'react';
import { SettingsPlan, BusinessDetails, TeamMembers } from './Subpages';
import { AnimatePresence } from 'framer-motion';
import { PaymentMethods } from './Subpages/PaymentMethods';
import { useSearchParams } from 'react-router-dom';
import { OptionInterface } from '@app/models/util';
import AddCardIcon from '@app/assets/icons/add-card.svg?react';
import UserPlusIcon from '@app/assets/icons/user-plus.svg?react';
import BulbIcon from '@app/assets/icons/menu/bulb.svg?react';
import { usePermissions } from '@app/contexts/PermissionContext';
import { PermissionKey } from '@app/models/business';

export const InviteMemberContext = createContext<{
  showInviteModal: boolean;
  setShowInviteModal: (show: boolean) => void;
}>({ showInviteModal: false, setShowInviteModal: () => {} });

export const useInviteMemberContext = () => useContext(InviteMemberContext);

const AddPaymentMethodContext = createContext<{
  showAddPaymentMethod: boolean;
  setShowAddPaymentMethod: (show: boolean) => void;
}>({ showAddPaymentMethod: false, setShowAddPaymentMethod: () => {} });

export const useAddPaymentMethodContext = () =>
  useContext(AddPaymentMethodContext);

export const Settings = () => {
  const { activeSubpage, setActivePage, handleTabChange, isLoading } = useSettingsPage();
  const [searchParams] = useSearchParams();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const { can } = usePermissions();

  const tabPermissions = new Map<SettingTabs, PermissionKey>([
    [SettingTabs.MEMBERS, 'editTeamMembers'],
    [SettingTabs.PAYMENT_METHODS, 'editPaymentMethods'],
    [SettingTabs.PLAN, 'changePackage'],
  ]);

  const filteredTabs = useMemo(() => {
    return SettingsTabs.filter((tab) => {
      const permission = tabPermissions.get(tab.value as SettingTabs);
      return !permission || can(permission);
    });
  }, [can]);

  const render = useMemo(() => {
    switch (activeSubpage) {
      case SettingTabs.DETAILS:
        return <BusinessDetails />;
      case SettingTabs.MEMBERS:
        return <TeamMembers />;
      case SettingTabs.PAYMENT_METHODS:
        return <PaymentMethods />;
      case SettingTabs.PLAN:
        return <SettingsPlan />;
      default:
        return <BusinessDetails />;
    }
  }, [activeSubpage]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeSubpage) {
      const isAllowed = filteredTabs.some((tab) => tab.value === tabParam);
      if (isAllowed) {
        setActivePage(tabParam as SettingTabs);
      }
    }
  }, [searchParams, activeSubpage, setActivePage, filteredTabs]);

  useEffect(() => {
    const isCurrentTabAllowed = filteredTabs.some((tab) => tab.value === activeSubpage);
    if (!isCurrentTabAllowed && filteredTabs.length > 0) {
      setActivePage(filteredTabs[0].value as SettingTabs);
    }
  }, [filteredTabs, activeSubpage, setActivePage]);

  return (
    <div className=" min-h-full">
      <div className="bg-white border-b border-b-base-ghostWhite ">
        <div className="container mx-auto pt-4 px-4">
          <div className="flex justify-between items-start mb-2">
            <h2 className="font-bold text-[28px]">Settings</h2>
            {activeSubpage === SettingTabs.PLAN && (
              <div className="flex items-start gap-3 bg-brand-lightBlue3 rounded-xl p-4 max-w-sm">
                <BulbIcon
                  width={20}
                  height={20}
                  className="[&_path]:stroke-brand-baseBlue shrink-0 mt-0.5"
                />
                <p className="text-sm text-gray-600">
                  For plans that we offer if you need more seats, review
                  requests, or integrations to your personal CRM, etc. this is
                  available upon{' '}
                  <a
                    href={`mailto:${ContactUsEmail}`}
                    className="text-brand-baseBlue underline"
                  >
                    request.
                  </a>
                </p>
              </div>
            )}
            {activeSubpage === SettingTabs.PAYMENT_METHODS && can('editPaymentMethods') && (
              <Button
                leftNode={<AddCardIcon />}
                onClick={() => setShowAddPaymentMethod(true)}
                className="font-medium bg-blue-600 text-white px-4 py-3 rounded-lg flex items-center gap-2"
              >
                Add payment method
              </Button>
            )}
            {activeSubpage === SettingTabs.MEMBERS && can('editTeamMembers') && (
              <Button
                leftNode={<UserPlusIcon className="w-5 h-5" />}
                onClick={() => setShowInviteModal(true)}
              >
                Invite member
              </Button>
            )}
          </div>
          <Tabs
            tabs={filteredTabs as unknown as OptionInterface[]}
            onClick={handleTabChange}
            defaultValue={activeSubpage}
          />
        </div>
      </div>
      <div className="relative min-h-full">
        <InviteMemberContext.Provider value={{ showInviteModal, setShowInviteModal }}>
          <AddPaymentMethodContext.Provider
            value={{ showAddPaymentMethod, setShowAddPaymentMethod }}
          >
            {isLoading ? (
              <div className="container mx-auto p-4">
                <p className="text-base-raisinBlack font-bold">
                  Fetching new information...
                </p>
              </div>
            ) : (
              <div className="container mx-auto p-4">
                <AnimatePresence initial={false}>{render}</AnimatePresence>
              </div>
            )}
          </AddPaymentMethodContext.Provider>
        </InviteMemberContext.Provider>
      </div>
    </div>
  );
};
